import { StyledContainer } from "./styles";
import Modal from "react-modal";
import { useCallback, useMemo, useState } from "react";
import { useAppStore } from "../../../stores/appStore";
import { useSudowriteStore } from "../../../stores/sudowriteStore";
import { useHistory } from "react-router-dom";
import Auth from "../../../Auth";
import { FEATURE_GATE_OPTIONS } from "../../../shared/featureGates";

Modal.setAppElement("#root");

const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(55, 55, 55, 0.7)",
    zIndex: "100000001",
  },
  content: {
    top: "10%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    zIndex: "20000",
    width: "min(750px, calc(100vw - 32px))",
    maxHeight: "85vh",
    overflow: "auto",
    transform: "translate(-50%, 0)",
    borderRadius: "15px",
    border: "0px",
    padding: "0px",
    background: "var(--menu-color)",
    boxShadow: "var(--default-drop-shadow)",
  },
};

export default function DocumentAdminMenu({ open, close, document }) {
  const [YDocJSON, setYDocJSON] = useState(null);
  const [gateError, setGateError] = useState(null);
  const [pendingGate, setPendingGate] = useState(null);
  const isSW2 = useAppStore((state) => state.isSW2);
  const setIsSW2 = useAppStore((state) => state.setIsSW2);
  const userData = useSudowriteStore((state) => state.userData);
  const subscription = useSudowriteStore((state) => state.subscription);
  const history = useHistory();

  const features = subscription?.features || {};

  const showYDocJSON = () => {
    setYDocJSON(window.tiptap.getJSON());
  };

  const handleGateToggle = useCallback(async (gateKey, enabled) => {
    const sub = useSudowriteStore.getState().subscription;
    if (!sub?.userId) return;
    setGateError(null);
    setPendingGate(gateKey);
    try {
      // Express authorizes the admin and keeps paired feature flags in sync.
      const result = await Auth.fetchWithIdToken("/api/admin/editor/gate", { gateKey, enabled });
      if (!result.success) throw new Error(result.message || result.error || "Could not update gates.");
    } catch (err) {
      console.error("Failed to update feature gate", err);
      setGateError(err?.message || "Could not update gates. Check your connection and try again.");
    } finally {
      setPendingGate(null);
    }
  }, []);

  const gateRows = useMemo(
    () =>
      FEATURE_GATE_OPTIONS.map(({ value, label }) => (
        <div
          className="gate-row"
          key={value}
        >
          <label htmlFor={`doc-admin-gate-${value}`}>
            <input
              id={`doc-admin-gate-${value}`}
              type="checkbox"
              checked={!!features[value]}
              disabled={pendingGate !== null}
              onChange={(e) => handleGateToggle(value, e.target.checked)}
            />
            <span>{label}</span>
          </label>
        </div>
      )),
    [features, handleGateToggle, pendingGate],
  );

  return (
    <Modal
      isOpen={open}
      style={modalStyles}
    >
      <StyledContainer>
        <button
          type="button"
          onClick={close}
          className="close"
          aria-label="Close document admin"
        >
          close
        </button>
        <h2>Document Admin</h2>
        {document && (
          <div>
            <p>ID: {document.id}</p>
            <p>
              <button
                type="button"
                onClick={showYDocJSON}
              >
                Show YDoc JSON
              </button>
              {YDocJSON && (
                <div className="json">
                  <pre>{JSON.stringify(YDocJSON, null, 2)}</pre>
                </div>
              )}
            </p>
            <p>
              <label className="inline-check">
                Enable Sudowrite 2.0?
                <input
                  checked={isSW2}
                  onChange={(e) => setIsSW2(e.target.checked)}
                  type="checkbox"
                  aria-label="Enable Sudowrite 2.0"
                />
              </label>
            </p>

            <h3>Feature gates (this account)</h3>
            {subscription ? (
              <>
                <p className="gates-note">
                  Toggling Chat or Plugins also updates the paired ungated flag so access matches your plan type.
                </p>
                <div
                  className="gates-list"
                  role="group"
                  aria-label="Feature gates for this account"
                >
                  {gateRows}
                </div>
              </>
            ) : (
              <p className="gates-note">Subscription not loaded; gates unavailable.</p>
            )}
            {gateError && <p className="gate-error">{gateError}</p>}

            <p>
              <button
                type="button"
                onClick={() => {
                  userData.update({ onboarding: {} });
                  history.go(0);
                }}
              >
                Clear onboarding progress
              </button>
            </p>

            <p>
              <button
                type="button"
                onClick={() => {
                  userData.resetToDefaults();
                  history.go(0);
                }}
              >
                Reset User Data to defaults
              </button>
            </p>
          </div>
        )}
      </StyledContainer>
    </Modal>
  );
}
